import type { Chart, Options, Point, Series } from 'highcharts/highstock'
import { escapeHtml } from 'utils/escapeHtml'

// Class names shared between the chart options (the labelFormatter below) and the CSS that styles them
// (DashboardChart). Kept here so the two stay in sync.
export const LEGEND_ITEM_CLASS = 'dashboard-legend-item'

export const LEGEND_CHECKBOX_CLASS = 'dashboard-legend-checkbox'

export const LEGEND_SWATCH_CLASS = 'dashboard-legend-swatch'

export const LEGEND_ITEM_CHECKED_CLASS = 'is-checked'

type TLegendSeriesLike = {
  name: string
  visible?: boolean
  color?: unknown
  options: { color?: string; dashStyle?: string; marker?: { symbol?: string } }
}

// The legend mixes solid lines (trap, degree days), dashed lines (sprays, crop events, biofix) and
// band squares (spray windows). Derive the swatch shape from the series' own options.
const swatchModifier = (options: TLegendSeriesLike['options']): 'square' | 'dash' | 'line' => {
  if (options.marker?.symbol === 'square') return 'square'

  if (options.dashStyle && options.dashStyle !== 'Solid') return 'dash'

  return 'line'
}

// Renders one legend row as [checkbox][color swatch][name]. The swatch uses `currentColor`, so a single
// inline color drives every variant. The checked class reflects the series' current visibility — see
// syncLegendCheckboxes for why it's also re-applied on render.
// Must be a `function` (not an arrow) — Highcharts invokes labelFormatter with the series as `this`.
// eslint-disable-next-line local-rules/prefer-const-arrow-functions
export function legendCheckboxLabelFormatter(this: Point | Series): string {
  const series = this as unknown as TLegendSeriesLike
  const color = (typeof series.color === 'string' && series.color) || series.options.color || '#000000'
  const modifier = swatchModifier(series.options)
  const checkedClass = series.visible === false ? '' : ` ${LEGEND_ITEM_CHECKED_CLASS}`

  // The checkbox + swatch are decorative (the toggle lives on the Highcharts legend item, which isn't
  // keyboard-operable without the a11y module) — hide them from screen readers; the name carries the
  // label.
  return (
    `<span class="${LEGEND_ITEM_CLASS}${checkedClass}">` +
    `<span class="${LEGEND_CHECKBOX_CLASS}" aria-hidden="true"></span>` +
    `<span class="${LEGEND_SWATCH_CLASS} ${LEGEND_SWATCH_CLASS}--${modifier}" style="color:${color}" aria-hidden="true"></span>` +
    `<span>${escapeHtml(series.name)}</span>` +
    '</span>'
  )
}

// Highcharts caches legend labels and does NOT re-run labelFormatter when a series is toggled, so the
// checked state would otherwise freeze at its initial value. Re-apply it from each series' current
// visibility on every (re)render. Legend rows render in `allItems` order, matching the DOM order.
// Must be a `function` (not an arrow) — Highcharts invokes the render event with the chart as `this`.
// eslint-disable-next-line local-rules/prefer-const-arrow-functions
export function syncLegendCheckboxes(this: Chart): void {
  const items = this.legend?.allItems ?? []
  const nodes = this.container?.querySelectorAll(`.${LEGEND_ITEM_CLASS}`)

  if (!nodes) return

  items.forEach((item, i) => {
    const node = nodes[i]

    if (node) {
      node.classList.toggle(LEGEND_ITEM_CHECKED_CLASS, (item as { visible?: boolean }).visible !== false)
    }
  })
}

// Plain series-name label, used for the export legend (our screen labelFormatter returns HTML, which
// would render as literal markup in SVG text). SVG text parses markup too, so the name is escaped.
// Must be a `function` (not an arrow) — Highcharts invokes labelFormatter with the series as `this`.
// eslint-disable-next-line local-rules/prefer-const-arrow-functions
export function legendNameFormatter(this: Point | Series): string {
  return escapeHtml((this as unknown as { name: string }).name)
}

// The JPG export rasterizes the SVG only, so our useHTML legend (an HTML overlay) isn't captured.
// DashboardChart swaps to this plain SVG-native legend (default symbols + names) for the snapshot,
// then reverts. A static image doesn't need checkboxes anyway.
export const EXPORT_LEGEND_OPTIONS: Options['legend'] = {
  useHTML: false,
  symbolWidth: 16,
  symbolHeight: 12,
  symbolPadding: 4,
  labelFormatter: legendNameFormatter,
}
